// Per-device persistence. Storage can be missing or throw (private mode, blocked site data), so
// every access is guarded and the app works without it: it only loses seat recovery on reload.

export interface SavedSeat {
  token: string;
  seatId: string;
  name: string;
  seq: number;
}

export interface Prefs {
  haptics: boolean;
  sound: boolean;
}

const seatKey = (code: string) => `tc:seat:${code.toUpperCase()}`;
const NAME_KEY = "tc:name";
const PREFS_KEY = "tc:prefs";
const PASSWORD_KEY = "tc:password";
const DEFAULT_PREFS: Prefs = { haptics: true, sound: false };

function read<T>(key: string): T | null {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function write(key: string, value: unknown): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage unavailable: nothing to do */
  }
}

function remove(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* storage unavailable */
  }
}

export function loadSeat(code: string): SavedSeat | null {
  const s = read<SavedSeat>(seatKey(code));
  return s && typeof s.token === "string" && typeof s.seatId === "string" ? s : null;
}

export function saveSeat(code: string, seat: SavedSeat): void {
  write(seatKey(code), seat);
}

export function updateSeatSeq(code: string, seq: number): void {
  const s = loadSeat(code);
  if (s && seq > s.seq) write(seatKey(code), { ...s, seq });
}

export function clearSeat(code: string): void {
  remove(seatKey(code));
}

/** Rooms this device holds a seat in (for "continue" links on the home screen). */
export function listSavedSeats(): { code: string; seat: SavedSeat }[] {
  const out: { code: string; seat: SavedSeat }[] = [];
  try {
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (!key?.startsWith("tc:seat:")) continue;
      const code = key.slice("tc:seat:".length);
      const seat = loadSeat(code);
      if (seat) out.push({ code, seat });
    }
  } catch {
    /* storage unavailable */
  }
  return out;
}

export function loadName(): string {
  return read<string>(NAME_KEY) ?? "";
}

export function saveName(name: string): void {
  write(NAME_KEY, name);
}

export function loadPrefs(): Prefs {
  return { ...DEFAULT_PREFS, ...(read<Partial<Prefs>>(PREFS_KEY) ?? {}) };
}

export function savePrefs(prefs: Prefs): void {
  write(PREFS_KEY, prefs);
}

/** The site password this device last entered (PasswordGate), or "" if none. */
export function loadPassword(): string {
  return read<string>(PASSWORD_KEY) ?? "";
}

export function savePassword(password: string): void {
  write(PASSWORD_KEY, password);
}
